/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      route — online payment status/retry page; also the deep-link
 *              landing target for unauthenticated users carrying an
 *              access_token
 *   extends:   Route
 *   template:  none
 *   model:     loads the `payment` record by params.id
 *   key:       beforeModel (deep-link access_token fallback auth), model,
 *              setupController (starts the controller's pollPaymentTask),
 *              resetController (cancels it on exit)
 *   uses:      services: session, store; controllerFor('application')
 *   siblings:  controllers/payment-status.js
 *   tests:     tests/unit/routes/payment-status-route-test.js,
 *              tests/acceptance/payment-status-test.js
 *   gotchas:   `access_token` QP is owned by the application controller (see
 *              controllers/application.js queryParams), so this route reads
 *              it from `transition.to.queryParams` rather than `paramsFor`
 *              (which would require declaring the QP here too and collide).
 *              Fallback only: `session.bootSessionTask` normally consumes the
 *              same query param in the APPLICATION route's beforeModel first.
 *
 * Payment-status/retry route for the e-commerce storefront.
 */
import Route from '@ember/routing/route';
import { set } from '@ember/object';
import { inject as service } from '@ember/service';

export default class PaymentStatusRoute extends Route {
  @service session;
  @service store;

  breadCrumb = {
    title: 'Stato pagamento',
  };

  beforeModel(transition) {
    // `access_token` QP is owned by the application controller, so read the
    // deep-link token from the transition's query params (not paramsFor, which
    // requires this route to declare the QP and would collide).
    //
    // Fallback only: the session's `bootSessionTask` normally consumes the same
    // query param in the APPLICATION route's beforeModel, i.e. before this one.
    // This route used to also raise an `tmpTokenLogin` flag here to reveal the
    // sign-up offer, which for that very reason could never be reached; the
    // template now decides from the contact itself (`session.me.username`).
    const accessToken = transition?.to?.queryParams?.access_token;
    if (accessToken && !this.session.isAuthenticated()) {
      this.session.authenticate(accessToken, null, false);
    }
  }

  model(params) {
    return this.store.findRecord('payment', params.id);
  }

  setupController(controller, model) {
    super.setupController(controller, model);
    controller.setProperties({
      content: model,
      newPayment: false,
      bonicifoMailSent: false,
      verifyTimedOut: false,
    });
    // While the bank's answer is pending the page re-reads the payment by itself
    // (the «checking» outcome offers no retry, see lib/payment-outcome).
    controller.pollPaymentTask.perform();

    const application = this.controllerFor('application');
    if (!application.get('content.me')) {
      set(application, 'content.me', this.store.findRecord('contact', 'me'));
    }
  }

  resetController(controller, isExiting) {
    super.resetController?.(...arguments);
    if (isExiting) controller.pollPaymentTask.cancelAll();
  }
}
